import React, { FC, useMemo } from 'react'

import Box from '@mui/material/Box'
import Avatar from '@mui/material/Avatar'
import prettyBytes from 'pretty-bytes'
import DataGrid2, { IDataGrid2_Column } from '../datagrid2/DataGrid'
import FAIcon from '../widgets/FAIcon'
import ClickLink from '../widgets/ClickLink'
import {
  StorageFile,
} from '@kaidam/fc-shared/src/utils/storage'

import {
  IForm_File,
} from '@kaidam/fc-shared/src/types/schema/form'

import icons from '../../icons'
const FolderIcon = icons.folder

interface FileDataGridProps {
  files: IForm_File[],
  token: string,
  loading: boolean,
  onEditItem?: {
    (file: IForm_File): void,
  },
  onDeleteItem?: {
    (file: IForm_File): void,
  },
  // if this is given then it's "chooser" mode
  // i.e. folders are just for navigating
  onChooseItem?: {
    (file: IForm_File): void,
  },
  onViewFolder: {
    (folder: string): void,
  }
}

const FileDataGrid: FC<React.PropsWithChildren<FileDataGridProps>> = ({
  files,
  token,
  loading,
  onChooseItem,
  onViewFolder,
  onEditItem,
  onDeleteItem,
}) => {


  const columns: IDataGrid2_Column<IForm_File>[] = [
    {
      name: 'icon',
      header: '',
      sortable: false,
      defaultWidth: 100,
      render: ({ data }) => {
        const storageFile = StorageFile(data, token)
        const isAFolder = storageFile.isFolder()
        let icon = null

        if(storageFile.isImage() && storageFile.mediaLink()) {
          icon = (
            <div>
              <Box
                component={'img'}
                sx={{
                  maxWidth: '60px',
                  maxHeight: '60px',
                  border: '1px solid',
                  borderColor: 'secondary.main',
                }}
                src={ storageFile.mediaLinkUrl() }
              />
            </div>
          )
        }
        else if(isAFolder) {
          icon = (
            <Avatar>
              <FolderIcon />
            </Avatar>
          )
        }
        else {
          icon = (
            <span className={`fiv-viv fiv-size-md fiv-icon-${storageFile.extension()}`}></span>
          )
        }

        return icon
      }
    },
    {
      name: 'name',
      header: 'Name',
      sortable: false,
      defaultFlex: 1,
      render: ({ data }) => {
        const storageFile = StorageFile(data, token)
        const isAFolder = storageFile.isFolder()
        return isAFolder ? (
          <ClickLink
            onClick={ () => onViewFolder(data.pointer.path) }
          >
            { storageFile.displayName() }
          </ClickLink>
        ) : (
          <a href={ storageFile.url() } target="_blank">
            { storageFile.displayName() }
          </a>
        )
      }
    },
    {
      name: 'updated',
      header: 'Updated',
      sortable: false,
      defaultFlex: 1,
      render: ({ data }) => {
        const storageFile = StorageFile(data, token)
        const isAFolder = storageFile.isFolder()
        return !isAFolder && data.meta.updated ? (
          <Box
            sx={{
              fontSize: '0.9em',
            }}
          >
            { new Date(data.meta.updated).toLocaleString() }
          </Box>
        ) : null
      }
    },
    {
      name: 'size',
      header: 'Size',
      sortable: false,
      defaultFlex: 1,
      render: ({ data }) => {
        const storageFile = StorageFile(data, token)
        const isAFolder = storageFile.isFolder()
        return isAFolder || (data.meta.size && isNaN(parseInt(data.meta.size))) ? '' : (
          <Box
            sx={{
              fontSize: '0.9em',
            }}
          >
            { prettyBytes(parseInt(data.meta.size || '0')) }
          </Box>
        )
      }
    },
    {
      name: 'actions',
      header: 'Actions',
      sortable: false,
      minWidth: 200,
      defaultWidth: 200,
      textAlign: 'end',
      render: ({ data }) => {
        if(onChooseItem) {
          return (
            <FAIcon
              iconSx={{
                pr: 2,
              }}
              tooltip="select"
              iconName="fa-circle-check"
              fontSize="1.2rem"
              onClick={ () => onChooseItem(data) }
            />
          )
        } else {
          return (
            <>
              {
                onDeleteItem && (
                  <FAIcon
                    iconSx={{
                      pr: 2,
                    }}
                    tooltip="delete"
                    iconName="delete"
                    fontSize="1.2rem"
                    onClick={ () => onDeleteItem(data) }
                  />
                )
              }
              {/* {
                onEditItem && (
                  <FAIcon
                    iconSx={{
                      pr: 2,
                    }}
                    tooltip="edit"
                    iconName="edit"
                    fontSize="1.2rem"
                    onClick={ () => onEditItem(data) }
                  />
                )
              } */}
            </>
          )
        } 
      }
    },
  ]

  return (
    <DataGrid2
      autoSort
      userSelect
      rows={ files }
      columns={ columns }
      loading={ loading }
      innerSx={{
        minHeight: '800px',
      }}
    />
  )
}

export default FileDataGrid